import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';

import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import { Store } from '@ngxs/store';
import { Observable, catchError, defer, map, of, switchMap, tap } from 'rxjs';
import { AppBootstrapLoadingService } from 'src/app/core/layout/services/app-bootstrap-loading.service';
import { AppErrorCode } from 'src/app/errors/enums/app-error-code.enum';
import { AppErrorNavigationService } from 'src/app/errors/services/app-error-navigation.service';
import { environment } from 'src/environments/environment';

import gcpEnv from '../../../../environments/environment.gcp.json';
import { PortfolioStorage } from '../../enums/portfolio-storage.enum';
import {
  Data,
  LivPortfolioErrorResponse,
  Model
} from '../../models/liv-portfolio-response-protocol.model';
import { StrapiUserSessionModel } from '../../models/strapi-user-session.model';
import { GeneralService } from '../../services/general.service';
import { StorageService } from '../../services/storage.service';
import { UserProfileBootstrapService } from '../../services/user-profile-bootstrap.service';
import { SetAccessTokenAction } from '../../store/session/session.action';
import { UserState } from '../../store/user/user.state';

interface Tokens {
  accessToken: string;
  portalToken: string;
}

const LOCAL_USER_MAIL = gcpEnv['mail'] || ('' as const);
const LOCAL_USER_PASSWORD = gcpEnv['password'] || ('' as const);

@Injectable({
  providedIn: 'root'
})
export class InterceptorGuard {
  constructor(
    private readonly router: Router,
    private readonly i18n: I18nService,
    private readonly store: Store,
    private readonly storageService: StorageService,
    private readonly bootstrapLoading: AppBootstrapLoadingService,
    private readonly httpClient: HttpClient,
    private readonly generalService: GeneralService,
    private readonly userProfileBootstrapService: UserProfileBootstrapService,
    private readonly appErrorNavigation: AppErrorNavigationService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    this.bootstrapLoading.show();

    const tokens =
      this.getTokensFromRoute(route) || this.getTokensFromStorage();

    const result$ = !tokens
      ? this.authenticateLocalUserOrRedirect()
      : this.canAccess(tokens, state.url);

    return result$.pipe(
      tap((result) => this.clearBootstrapIfRedirect(result)),
      catchError((error: LivPortfolioErrorResponse) => this.handleError(error))
    );
  }

  private clearBootstrapIfRedirect(result: boolean | UrlTree): void {
    if (result !== true) {
      this.bootstrapLoading.hide();
    }
  }

  private authenticateLocalUserOrRedirect(): Observable<boolean | UrlTree> {
    if (this.validateLocalOrigin(window.location.href)) {
      return this.authenticateLocalUser();
    }

    return of(this.getAuthUrlTree(AppErrorCode.Session));
  }

  private getTokensFromRoute(route: ActivatedRouteSnapshot): Tokens | null {
    const accessToken = route.queryParamMap.get('liv-token-pto');
    const portalToken = route.queryParamMap.get('liv-token-ptl');
    return this.areTokensValid(accessToken, portalToken)
      ? { accessToken, portalToken }
      : null;
  }

  private getTokensFromStorage(): Tokens | null {
    const accessToken = this.storageService.get<string>(
      PortfolioStorage.AccessToken
    );
    const portalToken = this.storageService.get<string>(
      PortfolioStorage.PortalToken
    );
    return this.areTokensValid(accessToken, portalToken)
      ? { accessToken, portalToken }
      : null;
  }

  private areTokensValid(
    accessToken: string | null,
    portalToken: string | null
  ): boolean {
    return (
      accessToken !== null &&
      portalToken !== null &&
      accessToken.trim().length > 0 &&
      portalToken.trim().length > 0
    );
  }

  private canAccess(
    tokens: Tokens,
    targetUrl: string
  ): Observable<boolean | UrlTree> {
    return defer(() => {
      this.storageService.save<string>(
        PortfolioStorage.AccessToken,
        tokens.accessToken
      );
      this.storageService.save<string>(
        PortfolioStorage.PortalToken,
        tokens.portalToken
      );
      this.store.dispatch(new SetAccessTokenAction(tokens.accessToken));

      return this.userProfileBootstrapService.ensureLoaded(targetUrl).pipe(
        map((result) => this.syncLanguageFromUserCountry(result, targetUrl)),
        catchError((error: LivPortfolioErrorResponse) =>
          this.handleError(error)
        )
      );
    });
  }

  private authenticateLocalUser(): Observable<boolean | UrlTree> {
    return this.httpClient
      .post<{ data: { token: string } }>(`${environment.apiLiv}/login`, {
        login: LOCAL_USER_MAIL,
        password: LOCAL_USER_PASSWORD
      })
      .pipe(
        switchMap((user) =>
          this.fetchUserData(user.data.token).pipe(
            map(({ data }) => ({ user, data }))
          )
        ),
        switchMap(({ data, user }) =>
          this.handleAuthenticationResponse(data, user)
        ),
        catchError((error: LivPortfolioErrorResponse) =>
          this.handleError(error)
        )
      );
  }

  private handleAuthenticationResponse(
    data: Model<StrapiUserSessionModel>,
    user: { data: { token: string } }
  ): Observable<boolean | UrlTree> {
    if (!data.attributes) {
      return of(this.getAuthUrlTree(AppErrorCode.Session));
    }

    this.storageService.save(
      PortfolioStorage.RunningInDevMode as string,
      new Date().toISOString()
    );

    const { jwt } = data.attributes;
    return this.canAccess(
      { accessToken: jwt, portalToken: user.data.token },
      window.location.pathname + window.location.search
    );
  }

  private fetchUserData(
    token: string
  ): Observable<Data<StrapiUserSessionModel>> {
    return this.generalService.getAuthToken(token);
  }

  private handleError(
    error: LivPortfolioErrorResponse
  ): Observable<boolean | UrlTree> {
    console.error('An error occurred:', error);
    this.storageService.deleteAll();
    this.bootstrapLoading.hide();

    const errorCode =
      error?.status === 401 ? AppErrorCode.Session : AppErrorCode.Unexpected;

    return of(this.getAuthUrlTree(errorCode));
  }

  private validateLocalOrigin(url: string): boolean {
    const parsedUrl = new URL(url);
    const regex = /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?/;
    return regex.test(parsedUrl.origin);
  }

  private getAuthUrlTree(error: AppErrorCode): UrlTree {
    return this.appErrorNavigation.createUrlTree(error);
  }

  /**
   * Alinha prefixo de idioma na URL e i18n ao país do utilizador; se a liv-i18n
   * marcar reload completo, o guard deve falhar até o browser recarregar (igual
   * ao fluxo em liv-portfolio-front-visao-familia).
   */
  private syncLanguageFromUserCountry(
    result: boolean | UrlTree,
    targetUrl: string
  ): boolean | UrlTree {
    if (result !== true) {
      return result;
    }

    const country = this.store.selectSnapshot(UserState.country);
    const allowedLanguage = country
      ? this.i18n.getLanguageFromCountry(country)
      : null;
    const parsedUrl = this.router.parseUrl(targetUrl);
    const primarySegments = parsedUrl.root.children['primary']?.segments ?? [];
    const currentRouteLanguage = primarySegments[0]?.path ?? null;

    if (
      allowedLanguage &&
      currentRouteLanguage &&
      allowedLanguage !== currentRouteLanguage
    ) {
      const scheduled = this.i18n.switchLanguage(allowedLanguage, {
        navigationUrl: targetUrl
      });
      return !scheduled;
    }

    const hasScheduledReload = this.i18n.syncLanguageFromCountry(country, {
      navigationUrl: targetUrl
    });

    return !hasScheduledReload;
  }
}

export const interceptorGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => inject(InterceptorGuard).canActivate(route, state);
